<template>
  <div
    class="IUserAvatar"
    :class="[
      size ? `IUserAvatar--${size}` : '',
      isSquare ? 'tw-rounded-none' : '',
    ]"
  >
    <template v-if="$slots.default">
      <slot />
    </template>
    <template v-else>
      <div
        v-if="avatarUrl"
        class="ImageWrapper"
        :class="[
          isSquare ? 'tw-rounded-none' : '',
        ]"
      >
        <img
          :src="avatarUrl"
          :alt="fullname"
          loading="lazy"
        >
      </div>
      <span v-else-if="initials">
        {{ initials }}
      </span>
      <button
        v-if="isEditable"
        type="button"
        class="IconEditWrapper"
      >
        <icon-edit />
      </button>
      <div
        v-if="status || status === 0"
        class="Status"
        :class="statusClass"
      />
    </template>
  </div>
</template>

<script>
export default {
  props: {
    fullname: {
      type: String,
      required: true,
    },
    avatar: {
      type: String,
      required: false,
      default: null,
    },
    size: {
      type: String,
      default: 'default',
    },
    status: {
      type: Number,
      default: null,
    },
    isEditable: {
      type: Boolean,
      default: false,
    },
    isSquare: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    initials() {
      if (!(this.fullname && this.fullname.trim().length)) {
        return false;
      }

      const fullnameSplit = this.fullname
        .split(' ')
        .map((word) => (word[0] ? word[0].toUpperCase() : ''));

      return `${fullnameSplit.shift() || ''}${fullnameSplit.pop() || ''}`;
    },
    avatarUrl() {
      const avatar = this.avatar;
      if (avatar && !avatar?.includes('://')) {
        return `${this.$baseHost}/${avatar}`;
      }

      return avatar;
    },
    statusClass() {
      if (this.status === 20) {
        return 'tw-bg-success';
      }
      if (this.status === 10) {
        return 'tw-bg-permit-1';
      }
      // There is no permit status with such value, adding this for purpose of having red badge color
      if (this.status === 1000) {
        return 'tw-bg-primary';
      }
      return 'tw-bg-inactive';
    },
  },
};
</script>

<style lang="scss" scoped>
.IUserAvatar {
  @apply tw-inline-flex tw-items-center tw-justify-center tw-bg-gray-dark tw-flex-shrink-0;
  @apply tw-text-xl tw-font-bold tw-leading-none tw-text-white;
  @apply tw-w-14 tw-h-14 tw-relative tw-rounded-xl;
  @apply dark:tw-bg-dm-gray tw-bg-opacity-70;

  .ImageWrapper {
    @apply tw-rounded-xl tw-overflow-hidden;
  }

  .Status {
    @apply tw-w-5 tw-h-5 tw-border-4 tw-border-white tw-bg-success tw-rounded-full tw-absolute tw--bottom-1 tw--right-1;
    @apply dark:tw-border-dm;
  }

  .IconEditWrapper {
    @apply tw-rounded-xl tw-overflow-hidden tw-bg-gray-dark tw-bg-opacity-70 tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-transition;
    @apply dark:tw-bg-dm-gray-dark;
    @apply tw-opacity-0;

    svg {
      @apply tw-w-4 tw-h-4;
    }
  }

  &:hover {
    .IconEditWrapper {
      @apply tw-opacity-100;
    }
  }

  &--large {
    @apply tw-w-20 tw-h-20 tw-rounded-3xl tw-text-3xl;

    .ImageWrapper {
      @apply tw-rounded-3xl;
    }

    .IconEditWrapper {
      @apply tw-rounded-3xl;

      svg {
        @apply tw-w-8 tw-h-8;
      }
    }

    .IsOnline {
      @apply tw--bottom-0.5 tw--right-0.5;
    }
  }

  &--small {
    @apply tw-w-12 tw-h-12;
  }

  &--mini {
    @apply tw-w-10 tw-h-10 tw-text-base;
  }
}
</style>
